import type React from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { User, Users } from 'lucide-react';
import { cn } from '@/utils/cn';
import { usePlayerNameResolver } from '@/contexts/PlayerNamesContext';
import type { PlayerColor } from './playerColors';

export type SelectMode = 'players' | 'teams';

export interface HeaderTeam {
  teamId: string;
  /** Player ids, already ordered (left role first). */
  players: string[];
}

interface TeamHeaderProps {
  /** In Depth: interactive selector. Visão Geral: read-only roster (no rail,
      no hint, every player lit, taps disabled). */
  interactive: boolean;
  /** Whether the selector picks individual players or whole teams. */
  mode: SelectMode;
  onModeChange: (mode: SelectMode) => void;
  teamLeft: HeaderTeam;
  teamRight: HeaderTeam;
  playerCrops: Record<string, string>;
  /** Currently-selected player ids (1..4) — players mode. */
  selectedIds: string[];
  /** Stable identity color per player id. */
  colors: Record<string, PlayerColor>;
  roles?: Record<string, { primary_role: string }>;
  onToggle: (playerId: string) => void;
  /** The player the report belongs to: always part of the comparison, so their
      chip is lit and inert in players mode. Null in tournament mode (no owner). */
  lockedPlayerId?: string | null;
  /** Toggle the whole roster ⇄ the report's owner (players mode only). */
  onToggleAll: () => void;
  /** The focused team — teams mode is single-select; both are always compared. */
  focusTeamId: string;
  /** Stable identity color per team id. */
  teamColors: Record<string, PlayerColor>;
  onSelectTeam: (teamId: string) => void;
}

// Players / Teams switch as a full-height vertical rail flush to the header's
// left edge — its own column, so it reads as part of the header chrome instead
// of crowding the selector. The two segments split the header's full height.
const ModeRail: React.FC<{
  mode: SelectMode;
  onModeChange: (m: SelectMode) => void;
}> = ({ mode, onModeChange }) => {
  const { t } = useTranslation('tournament');
  const items: { key: SelectMode; Icon: typeof User }[] = [
    { key: 'players', Icon: User },
    { key: 'teams', Icon: Users },
  ];
  return (
    <div className="relative flex w-9 shrink-0 flex-col self-stretch border-r border-white/10">
      {/* Sliding active thumb — animates between halves so the rail reads as a
          switch; its cyan inner edge marks the active side (FIP accent). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-white transition-transform duration-300 ease-out"
        style={{
          transform: mode === 'teams' ? 'translateY(100%)' : 'translateY(0)',
        }}
      >
        <span className="absolute right-0 top-0 h-full w-[2.5px] bg-[color:var(--fip-cyan)]" />
      </div>

      {items.map(({ key, Icon }) => {
        const active = mode === key;
        return (
          <button
            key={key}
            type="button"
            data-tour-press={`mode-${key}`}
            onClick={() => onModeChange(key)}
            aria-pressed={active}
            aria-label={t(`mode.${key}` as never)}
            className={cn(
              'relative z-10 flex flex-1 items-center justify-center gap-1 pl-1.5 outline-none transition-colors duration-200',
              'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--fip-cyan)]',
              active
                ? 'text-[color:var(--fip-navy)]'
                : 'text-white/45 hover:text-white/80',
            )}
          >
            <Icon
              size={14}
              strokeWidth={active ? 2.5 : 1.9}
              style={{ transform: 'rotate(-90deg)' }}
            />
            <span
              className="text-[8px] uppercase tracking-[0.14em]"
              style={{
                fontFamily: 'var(--f-label)',
                fontWeight: 600,
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                // Breathing room so the vertical label doesn't crowd the cyan
                // active-selection bar on the rail's right edge.
                marginRight: '3px',
              }}
            >
              {t(`mode.${key}` as never)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// Shared spring for every mode transition — soft, quick, no bounce.
const SPRING = {
  type: 'spring',
  stiffness: 320,
  damping: 32,
  mass: 0.7,
} as const;

// One player avatar. The SAME instance is kept across modes (stable key in a
// non-branching tree) so framer-motion's `layout` animates its position when
// the cluster reflows: avatars spread apart for Players, slide together to
// overlap for Teams. Players mode: tap selects this player + shows its
// name/role. Teams mode: tap focuses the whole team; the per-player label
// crossfades out.
const ClusterAvatar: React.FC<{
  /** Player id — the guided tour aims its simulated taps at this. */
  playerId: string;
  crop?: string;
  role?: string;
  name: string;
  active: boolean;
  ringColor: string;
  isTeams: boolean;
  /** False in Visão Geral: a read-only roster (no tap, every player lit). */
  interactive: boolean;
  /** The report's owner in players mode: selected, and not tappable off. */
  locked?: boolean;
  /** Force the name lit (true) or dim (false); undefined defers to `lit`. */
  strongName?: boolean;
  onClick?: () => void;
}> = ({
  playerId,
  crop,
  role,
  name,
  active,
  ringColor,
  isTeams,
  interactive,
  locked = false,
  strongName,
  onClick,
}) => {
  const { t } = useTranslation('tournament');
  // In display mode (Visão Geral) every player is shown at full strength with
  // a soft identity ring — a roster, not a selection.
  const lit = active || !interactive;
  // The NAME is emphasised for the viewer only, so "Tu" is findable at a
  // glance among four labels that otherwise look alike. Selection keeps being
  // shown by the ring and opacity, not by the name. Tournament mode has no
  // viewer, so there it falls back to lighting every active name as before.
  const nameLit = strongName ?? lit;
  return (
    <motion.button
      layout
      layoutDependency={isTeams}
      type="button"
      data-tour-player={playerId}
      onClick={onClick}
      aria-pressed={interactive ? active : undefined}
      tabIndex={interactive && !locked ? undefined : -1}
      transition={SPRING}
      className={cn(
        'relative flex flex-col items-center gap-1.5 outline-none',
        'focus-visible:ring-2 focus-visible:ring-white/40',
        isTeams ? '' : 'min-w-0 flex-1',
        lit ? 'z-10' : 'z-0',
        (!interactive || locked) && 'cursor-default',
        interactive && !active && (isTeams ? 'opacity-90' : 'opacity-70'),
      )}
    >
      <motion.span
        layout
        layoutDependency={isTeams}
        transition={SPRING}
        className={cn(
          'relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-white/10 transition-[box-shadow,filter] duration-300',
          !interactive
            ? 'ring-[3px]'
            : active
              ? 'ring-4 ring-offset-2 ring-offset-[color:var(--fip-navy)]'
              : 'ring-2 ring-white/25 grayscale-[35%]',
        )}
        style={
          lit
            ? ({ '--tw-ring-color': ringColor } as React.CSSProperties)
            : undefined
        }
      >
        {crop ? (
          <img
            src={crop}
            alt={name}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <User className="h-5 w-5 text-white/70" />
        )}
      </motion.span>

      <AnimatePresence mode="popLayout" initial={false}>
        {!isTeams && (
          <motion.span
            key="plabel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: 0.22, delay: 0.12 }}
            className="flex flex-col items-center gap-0.5"
          >
            <span
              className="max-w-[68px] truncate text-[10.5px] uppercase leading-tight tracking-[0.04em]"
              style={{
                fontFamily: 'var(--f-label)',
                fontWeight: nameLit ? 600 : 400,
                // Lit names use the player's identity color. On a light/blue
                // report header the muted identity tones wash out, so the report
                // theme defines --tr-label-strong (white) to win; FIP leaves it
                // unset and falls back to the identity color (unchanged).
                color: nameLit
                  ? `var(--tr-label-strong, ${ringColor})`
                  : 'var(--tr-label-dim, rgba(255,255,255,0.85))',
              }}
              title={name}
            >
              {name}
            </span>
            {role && (
              <span
                className="text-[8px] uppercase tracking-[0.18em]"
                style={{
                  fontFamily: 'var(--f-label)',
                  color: lit
                    ? `var(--tr-label-strong, ${ringColor})`
                    : 'var(--tr-label-dim-soft, rgba(255,255,255,0.55))',
                }}
              >
                {/* Always the player's own-perspective role — Esquerda/Direita
                    as the player experiences the court, never camera-relative. */}
                {t(role === 'left' ? 'role.left' : 'role.right')}
              </span>
            )}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

// One team's two players, rendered the same way in both modes so the avatars
// can animate between the spread (Players) and overlapped (Teams) layouts. The
// team label crossfades in only in Teams mode.
const TeamCluster: React.FC<{
  team: HeaderTeam;
  isTeams: boolean;
  interactive: boolean;
  playerCrops: Record<string, string>;
  selectedIds: string[];
  colors: Record<string, PlayerColor>;
  roles?: Record<string, { primary_role: string }>;
  teamLabel: string;
  teamColor: PlayerColor;
  isFocusTeam: boolean;
  lockedPlayerId?: string | null;
  onToggle: (id: string) => void;
  onSelectTeam: (id: string) => void;
}> = ({
  team,
  isTeams,
  interactive,
  playerCrops,
  selectedIds,
  colors,
  roles,
  teamLabel,
  teamColor,
  isFocusTeam,
  lockedPlayerId,
  onToggle,
  onSelectTeam,
}) => {
  const resolveName = usePlayerNameResolver();
  return (
    <motion.div
      layout
      layoutDependency={isTeams}
      transition={SPRING}
      className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
    >
      <motion.div
        layout
        layoutDependency={isTeams}
        transition={SPRING}
        className={cn(
          'flex items-start',
          isTeams ? 'justify-center -space-x-3' : 'w-full justify-around gap-1',
        )}
      >
        {team.players.map((pid) => {
          const active = isTeams ? isFocusTeam : selectedIds.includes(pid);
          const ringColor = (isTeams ? teamColor : colors[pid]).solid;
          // Teams mode still focuses the whole team from this avatar, so the
          // lock only applies to the per-player selector.
          const locked = !isTeams && pid === lockedPlayerId;
          // The report names every player by their place in the block — Tu,
          // Parceiro, Esquerda, Direita — so a side underneath is either a
          // stutter (the opponents) or noise (your own pair). Tournament mode
          // has real names and no owner, so the side still earns its line.
          const showRole = !lockedPlayerId;
          return (
            <ClusterAvatar
              key={pid}
              playerId={pid}
              crop={playerCrops[pid]}
              role={showRole ? roles?.[pid]?.primary_role : undefined}
              name={resolveName(pid, { short: true })}
              active={active}
              ringColor={ringColor}
              isTeams={isTeams}
              interactive={interactive}
              locked={locked}
              strongName={lockedPlayerId ? pid === lockedPlayerId : undefined}
              onClick={
                interactive && !locked
                  ? () => (isTeams ? onSelectTeam(team.teamId) : onToggle(pid))
                  : undefined
              }
            />
          );
        })}
      </motion.div>

      <AnimatePresence mode="popLayout" initial={false}>
        {isTeams && (
          <motion.button
            key="tlabel"
            layout
            layoutDependency={isTeams}
            type="button"
            onClick={() => onSelectTeam(team.teamId)}
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 2 }}
            transition={{ duration: 0.16 }}
            className="text-[11px] uppercase leading-none tracking-[0.07em] outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            style={{
              fontFamily: 'var(--f-label)',
              fontWeight: isFocusTeam ? 600 : 400,
              color: isFocusTeam
                ? `var(--tr-label-strong, ${teamColor.solid})`
                : 'var(--tr-label-dim, rgba(255,255,255,0.8))',
            }}
          >
            {teamLabel}
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const VsDivider: React.FC<{ isTeams: boolean }> = ({ isTeams }) => (
  <motion.div
    layout
    layoutDependency={isTeams}
    transition={SPRING}
    className="flex flex-col items-center justify-center px-1.5 pt-1"
  >
    <span className="h-7 w-px bg-white/15" />
    <span
      className="my-0.5 text-[10px] font-bold tracking-[0.1em] text-white/45"
      style={{ fontFamily: 'var(--f-label)' }}
    >
      VS
    </span>
    <span className="h-7 w-px bg-white/15" />
  </motion.div>
);

const TeamHeader: React.FC<TeamHeaderProps> = ({
  interactive,
  mode,
  onModeChange,
  teamLeft,
  teamRight,
  playerCrops,
  selectedIds,
  colors,
  roles,
  onToggle,
  lockedPlayerId,
  onToggleAll,
  focusTeamId,
  teamColors,
  onSelectTeam,
}) => {
  const { t } = useTranslation('tournament');
  // Visão Geral always shows the players' table — never the teams layout.
  const isTeams = interactive && mode === 'teams';
  const allSelected = [...teamLeft.players, ...teamRight.players].every((id) =>
    selectedIds.includes(id),
  );

  return (
    <div className="flex items-stretch bg-[color:var(--fip-navy)]">
      {/* The Players/Teams rail collapses away in Visão Geral. */}
      <motion.div
        className="flex self-stretch overflow-hidden"
        initial={false}
        animate={{ width: interactive ? 36 : 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <ModeRail mode={mode} onModeChange={onModeChange} />
      </motion.div>

      <div className="min-w-0 flex-1 px-3 pb-2.5 pt-2">
        {/* Keep roster projection local. Inheriting the report-wide group makes
            sticky-header coordinates shift whenever selection changes content
            below the header, replaying the avatar layout spring while scrolled. */}
        <LayoutGroup inherit={false}>
          <div className="flex min-w-0 items-stretch">
            <TeamCluster
              team={teamLeft}
              isTeams={isTeams}
              interactive={interactive}
              playerCrops={playerCrops}
              selectedIds={selectedIds}
              colors={colors}
              roles={roles}
              teamLabel={t('label.teamA')}
              teamColor={teamColors[teamLeft.teamId]}
              isFocusTeam={focusTeamId === teamLeft.teamId}
              lockedPlayerId={lockedPlayerId}
              onToggle={onToggle}
              onSelectTeam={onSelectTeam}
            />
            <VsDivider isTeams={isTeams} />
            <TeamCluster
              team={teamRight}
              isTeams={isTeams}
              interactive={interactive}
              playerCrops={playerCrops}
              selectedIds={selectedIds}
              colors={colors}
              roles={roles}
              teamLabel={t('label.teamB')}
              teamColor={teamColors[teamRight.teamId]}
              isFocusTeam={focusTeamId === teamRight.teamId}
              lockedPlayerId={lockedPlayerId}
              onToggle={onToggle}
              onSelectTeam={onSelectTeam}
            />
          </div>
        </LayoutGroup>

        <AnimatePresence initial={false}>
          {interactive && (
            <motion.div
              key="hint"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <div
                className="mt-2 flex items-center justify-center gap-2 text-[8.5px] uppercase tracking-[0.22em] text-white/40"
                style={{ fontFamily: 'var(--f-label)' }}
              >
                <span>
                  {t(mode === 'teams' ? 'compareHintTeams' : 'compareHint')}
                </span>
                {/* Shortcut past tapping the roster in one by one: lit = all
                    compared, tap again to fall back to the report's owner
                    alone. Teams mode already compares both teams. */}
                {!isTeams && (
                  <button
                    type="button"
                    data-tour-press="select-all"
                    onClick={onToggleAll}
                    aria-pressed={allSelected}
                    className={cn(
                      // `uppercase` is repeated here on purpose: the row sets it
                      // on the parent, but it doesn't reach a <button>.
                      'rounded-full border px-2 py-[2px] uppercase tracking-[0.18em] outline-none transition-colors',
                      'focus-visible:ring-2 focus-visible:ring-white/40',
                      allSelected
                        ? 'border-white/60 bg-white/15 text-white'
                        : 'border-white/25 text-white/70 hover:border-white/50 hover:text-white',
                    )}
                  >
                    {t('selectAll')}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TeamHeader;
